import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { useApiFetch } from '../../shared/api'

/**
 * The signed-in user's own dogs, the source the add-dogs picker lists when
 * registering dogs on a planned hunt (CLUB-883). Everything here is exactly
 * what the register header needs: `ownerName` is the user's own display name,
 * resolved server-side so the client can build the header without a second
 * profile lookup, and `devices` are the dog's collar ids, carried through so a
 * pre-registered dog keeps its collar when the hunt starts.
 */
export interface MyDog {
  id: string
  name: string
  ownerName: string
  /** Packed ARGB as mobile stores it; null when the dog has no colour set. */
  colorArgb: number | null
  icon: string | null
  devices: string[]
  /** The dog's own visibility (DogVisibility wire string); null when unset. */
  visibility: string | null
}

interface MyDogsResponse {
  dogs: {
    id: string
    name: string
    ownerName: string | null
    colorArgb: number | null
    icon: string | null
    devices: string[]
    visibility: string | null
  }[]
}

export function useMyDogs() {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useQuery({
    queryKey: ['clubhouse', 'me', 'dogs', uid],
    queryFn: async (): Promise<MyDog[]> => {
      const r = await call<MyDogsResponse>('/clubhouse/me/dogs')
      return (r.dogs ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        // The API can return a null owner name (no name and no displayName on
        // the user doc); the register header needs a string, so fall back to ''
        // — the same salvage mobile's reader applies.
        ownerName: d.ownerName ?? '',
        colorArgb: d.colorArgb ?? null,
        icon: d.icon ?? null,
        devices: d.devices ?? [],
        visibility: d.visibility ?? null,
      }))
    },
    enabled: !!uid,
    staleTime: 60 * 1000,
  })
}
